import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useReducedMotion } from './hooks/useReducedMotion';

gsap.registerPlugin(ScrollTrigger);

const metrics = [
  { value: '15+', label: 'Psychological Principles', color: '#7C3AED' },
  { value: '4', label: 'Platforms Supported', color: '#60A5FA' },
  { value: '10', label: 'Tone Presets', color: '#34D399' },
  { value: '60s', label: 'Average Generation Time', color: '#F59E0B' },
  { value: '15', label: 'Day Free Trial', color: '#7C3AED' },
];

const statsStrip = [
  '15+ Psychology Principles',
  'Multi-Platform Publishing',
  'Direct Social Posting',
  'AI-Powered Content',
  'Transparent Decisions',
  'Learns Your Style',
  'Company Intelligence',
  'Persona Targeting',
  'Post Variations',
  'Saved Prompt Library',
];

// Duplicate for seamless loop
const marqueeItems = [...statsStrip, ...statsStrip];

export function SocialProofSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const prefersReduced = useReducedMotion();

  useEffect(() => {
    if (prefersReduced || !sectionRef.current) return;

    const ctx = gsap.context(() => {
      // Counter animation for metrics
      gsap.utils.toArray<HTMLElement>('.metric-value').forEach((el) => {
        const rawText = el.getAttribute('data-value') || '0';
        const numericPart = parseInt(rawText.replace(/\D/g, ''), 10);
        const suffix = rawText.replace(/[0-9]/g, '');

        const counter = { val: 0 };
        gsap.to(counter, {
          val: numericPart,
          duration: 2,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: el,
            start: 'top 85%',
            toggleActions: 'play none none none',
          },
          onUpdate: () => {
            el.textContent = Math.round(counter.val) + suffix;
          },
        });
      });
    }, sectionRef);

    return () => ctx.revert();
  }, [prefersReduced]);

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden bg-landing-bg"
      style={{
        paddingTop: 'clamp(80px, 10vh, 160px)',
        paddingBottom: 'clamp(80px, 10vh, 160px)',
      }}
    >
      {/* Top border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-landing-border" />

      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
        {/* Metrics strip */}
        <div className="mb-20 flex flex-wrap items-center justify-center gap-8 lg:gap-16">
          {metrics.map((m) => (
            <div key={m.label} className="text-center">
              <div
                className="metric-value"
                data-value={m.value}
                style={{
                  fontFamily: "'Space Grotesk', system-ui, sans-serif",
                  fontSize: 'clamp(2rem, 4vw + 0.5rem, 3.75rem)',
                  fontWeight: 700,
                  color: m.color,
                  lineHeight: 1.1,
                }}
              >
                {prefersReduced ? m.value : '0'}
              </div>
              <div
                className="mt-2 text-[#C8C6D2]"
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 'clamp(0.875rem, 0.9vw, 1rem)',
                  fontWeight: 400,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                }}
              >
                {m.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Marquee */}
      <div className="relative w-full overflow-hidden">
        <div
          className="flex gap-6"
          style={{
            animation: prefersReduced ? 'none' : 'marquee 30s linear infinite',
            width: 'max-content',
          }}
        >
          {marqueeItems.map((item, i) => (
            <div
              key={i}
              className="flex-shrink-0 px-6 py-3"
              style={{
                background: 'rgba(13, 13, 26, 0.7)',
                backdropFilter: 'blur(16px)',
                border: '1px solid rgba(232, 230, 240, 0.06)',
              }}
            >
              <span
                className="text-[#E8E6F0]/80 whitespace-nowrap"
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  letterSpacing: '0.03em',
                }}
              >
                {item}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom border */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-landing-border" />

      {/* CSS marquee animation */}
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </section>
  );
}
